/**
 * API Key Management - Admin Page (DC-629)
 *
 * Admin interface for managing API keys:
 * - View all API keys for the organization
 * - Create new API keys with scopes
 * - Revoke and rotate keys
 * - View usage statistics
 *
 * @version 1.0.0
 * @date 2026-01-04
 * @epic DC-6 Multi-Tenancy & IAM
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BRANDING } from '../../config/branding';
import { useAuthenticatedFetch } from '../../hooks/useAuth';
import './api-key-management.css';

// =============================================================================
// TYPES
// =============================================================================

type ApiKeyScope = 'read' | 'write' | 'admin';

interface ApiKey {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  key_prefix: string;
  scopes: ApiKeyScope[];
  rate_limit_per_hour: number;
  expires_at: string | null;
  is_active: boolean;
  last_used_at: string | null;
  usage_count: number;
  created_by: string;
  created_at: string;
  revoked_at: string | null;
}

interface ApiKeyUsageStats {
  total_requests: number;
  requests_last_hour: number;
  requests_last_24h: number;
  avg_response_time_ms: number | null;
  top_endpoints: Array<{ endpoint: string; count: number }>;
  error_rate: number;
}

interface Pagination {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

// Scope descriptions
const SCOPE_INFO: Record<ApiKeyScope, { label: string; description: string }> = {
  read: {
    label: 'Read',
    description: 'Read-only access (GET requests)',
  },
  write: {
    label: 'Write',
    description: 'Create and update data (POST, PUT, PATCH requests)',
  },
  admin: {
    label: 'Admin',
    description: 'Full access including delete operations and user management',
  },
};

// =============================================================================
// CREATE KEY MODAL
// =============================================================================

interface CreateKeyModalProps {
  onClose: () => void;
  onCreated: (key: string, record: ApiKey) => void;
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

const CreateKeyModal: React.FC<CreateKeyModalProps> = ({
  onClose,
  onCreated,
  authenticatedFetch,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>(['read']);
  const [rateLimit, setRateLimit] = useState(1000);
  const [expiresAt, setExpiresAt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleScopeChange = (scope: ApiKeyScope, checked: boolean) => {
    if (checked) {
      setScopes([...scopes, scope]);
    } else {
      setScopes(scopes.filter((s) => s !== scope));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (scopes.length === 0) {
      setError('At least one scope is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authenticatedFetch('/api/admin/api-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || undefined,
          scopes,
          rate_limit_per_hour: rateLimit,
          expires_at: expiresAt || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to create API key');
      }

      const data = await response.json();
      onCreated(data.data.key, data.data.record);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create API Key</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="error-container" style={{ padding: '0.75rem', marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="key-name">Name *</label>
              <input
                id="key-name"
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Production API Key"
                maxLength={100}
                required
              />
              <p className="helper-text">A descriptive name to identify this key</p>
            </div>

            <div className="form-group">
              <label htmlFor="key-description">Description</label>
              <input
                id="key-description"
                type="text"
                className="form-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>

            <div className="form-group">
              <label>Permissions *</label>
              <div className="checkbox-group">
                {(Object.keys(SCOPE_INFO) as ApiKeyScope[]).map((scope) => (
                  <div key={scope} className="checkbox-item">
                    <input
                      type="checkbox"
                      id={`scope-${scope}`}
                      checked={scopes.includes(scope)}
                      onChange={(e) => handleScopeChange(scope, e.target.checked)}
                    />
                    <label htmlFor={`scope-${scope}`} className="checkbox-label">
                      <span className="label-title">{SCOPE_INFO[scope].label}</span>
                      <span className="label-desc">{SCOPE_INFO[scope].description}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="rate-limit">Rate Limit (requests/hour)</label>
              <input
                id="rate-limit"
                type="number"
                className="form-input"
                value={rateLimit}
                onChange={(e) => setRateLimit(parseInt(e.target.value) || 1000)}
                min={1}
                max={100000}
              />
              <p className="helper-text">Maximum API requests allowed per hour</p>
            </div>

            <div className="form-group">
              <label htmlFor="expires-at">Expiration Date (optional)</label>
              <input
                id="expires-at"
                type="datetime-local"
                className="form-input"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
              <p className="helper-text">Leave empty for no expiration</p>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Key'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// KEY REVEAL MODAL
// =============================================================================

interface KeyRevealModalProps {
  apiKey: string;
  record: ApiKey;
  onClose: () => void;
}

const KeyRevealModal: React.FC<KeyRevealModalProps> = ({ apiKey, record, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>API Key Created</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          <div className="key-reveal">
            <div className="key-reveal-header">
              <span className="warning-icon">⚠️</span>
              <h4>Save this key now - it won't be shown again!</h4>
            </div>
            <div className="key-value">
              <code>{apiKey}</code>
              <button className={`copy-btn ${copied ? 'copied' : ''}`} onClick={handleCopy}>
                {copied ? '✓' : '📋'}
              </button>
            </div>
            <p className="key-warning">
              <span>🔒</span>
              Store this key securely. It cannot be retrieved after closing this dialog.
            </p>
          </div>

          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Name</span>
              <span className="detail-value">{record.name}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Scopes</span>
              <div className="scope-badges">
                {record.scopes.map((scope) => (
                  <span key={scope} className={`scope-badge ${scope}`}>
                    {scope}
                  </span>
                ))}
              </div>
            </div>
            <div className="detail-row">
              <span className="detail-label">Rate Limit</span>
              <span className="detail-value">{record.rate_limit_per_hour.toLocaleString()} req/hour</span>
            </div>
            {record.expires_at && (
              <div className="detail-row">
                <span className="detail-label">Expires</span>
                <span className="detail-value">{new Date(record.expires_at).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// DETAIL MODAL
// =============================================================================

interface DetailModalProps {
  apiKey: ApiKey;
  stats: ApiKeyUsageStats | null;
  onClose: () => void;
}

const DetailModal: React.FC<DetailModalProps> = ({ apiKey, stats, onClose }) => {
  const getStatus = () => {
    if (!apiKey.is_active) return { label: 'Revoked', class: 'revoked' };
    if (apiKey.expires_at && new Date(apiKey.expires_at) < new Date()) {
      return { label: 'Expired', class: 'expired' };
    }
    return { label: 'Active', class: 'active' };
  };

  const status = getStatus();

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>API Key Details</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Name</span>
              <span className="detail-value">{apiKey.name}</span>
            </div>
            {apiKey.description && (
              <div className="detail-row">
                <span className="detail-label">Description</span>
                <span className="detail-value">{apiKey.description}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Key Prefix</span>
              <span className="detail-value mono">{apiKey.key_prefix}...</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status</span>
              <span className={`status-badge ${status.class}`}>{status.label}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Scopes</span>
              <div className="scope-badges">
                {apiKey.scopes.map((scope) => (
                  <span key={scope} className={`scope-badge ${scope}`}>
                    {scope}
                  </span>
                ))}
              </div>
            </div>
            <div className="detail-row">
              <span className="detail-label">Rate Limit</span>
              <span className="detail-value">{apiKey.rate_limit_per_hour.toLocaleString()} req/hour</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Created</span>
              <span className="detail-value">{new Date(apiKey.created_at).toLocaleString()}</span>
            </div>
            {apiKey.expires_at && (
              <div className="detail-row">
                <span className="detail-label">Expires</span>
                <span className="detail-value">{new Date(apiKey.expires_at).toLocaleString()}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Last Used</span>
              <span className="detail-value">
                {apiKey.last_used_at ? new Date(apiKey.last_used_at).toLocaleString() : 'Never'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Total Requests</span>
              <span className="detail-value">{apiKey.usage_count.toLocaleString()}</span>
            </div>

            {stats && (
              <>
                <div className="detail-row">
                  <span className="detail-label">Last Hour</span>
                  <span className="detail-value">{stats.requests_last_hour.toLocaleString()} requests</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Last 24h</span>
                  <span className="detail-value">{stats.requests_last_24h.toLocaleString()} requests</span>
                </div>
                {stats.avg_response_time_ms !== null && (
                  <div className="detail-row">
                    <span className="detail-label">Avg Response</span>
                    <span className="detail-value">{stats.avg_response_time_ms.toFixed(0)} ms</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Error Rate</span>
                  <span className="detail-value">{(stats.error_rate * 100).toFixed(1)}%</span>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// CONFIRM MODAL
// =============================================================================

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmLabel: string;
  confirmClass?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  message,
  confirmLabel,
  confirmClass = 'btn-danger',
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onCancel}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          <p>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </button>
          <button className={`btn ${confirmClass}`} onClick={onConfirm} disabled={isLoading}>
            {isLoading ? 'Processing...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ApiKeyManagement: React.FC = () => {
  const { authFetch: authenticatedFetch } = useAuthenticatedFetch();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    limit: 20,
    offset: 0,
    has_more: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [revealedKey, setRevealedKey] = useState<{ key: string; record: ApiKey } | null>(null);
  const [detailKey, setDetailKey] = useState<{ key: ApiKey; stats: ApiKeyUsageStats | null } | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<ApiKey | null>(null);
  const [confirmRotate, setConfirmRotate] = useState<ApiKey | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch keys
  const fetchKeys = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        `/api/admin/api-keys?limit=${pagination.limit}&offset=${pagination.offset}&include_revoked=true`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch API keys');
      }

      const data = await response.json();
      setKeys(data.data.keys);
      setPagination(data.data.pagination);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  }, [authenticatedFetch, pagination.limit, pagination.offset]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  // Handle key creation
  const handleKeyCreated = (key: string, record: ApiKey) => {
    setShowCreateModal(false);
    setRevealedKey({ key, record });
    fetchKeys();
  };

  // View key details
  const handleViewDetails = async (apiKey: ApiKey) => {
    try {
      const response = await authenticatedFetch(`/api/admin/api-keys/${apiKey.id}/stats`);
      let stats: ApiKeyUsageStats | null = null;

      if (response.ok) {
        const data = await response.json();
        stats = data.data;
      }

      setDetailKey({ key: apiKey, stats });
    } catch {
      setDetailKey({ key: apiKey, stats: null });
    }
  };

  // Revoke key
  const handleRevoke = async () => {
    if (!confirmRevoke) return;

    setIsProcessing(true);
    try {
      const response = await authenticatedFetch(`/api/admin/api-keys/${confirmRevoke.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to revoke API key');
      }

      setConfirmRevoke(null);
      fetchKeys();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsProcessing(false);
    }
  };

  // Rotate key
  const handleRotate = async () => {
    if (!confirmRotate) return;

    setIsProcessing(true);
    try {
      const response = await authenticatedFetch(`/api/admin/api-keys/${confirmRotate.id}/rotate`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to rotate API key');
      }

      const data = await response.json();
      setConfirmRotate(null);
      setRevealedKey({ key: data.data.key, record: data.data.record });
      fetchKeys();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsProcessing(false);
    }
  };

  // Get status info
  const getStatus = (key: ApiKey) => {
    if (!key.is_active) return { label: 'Revoked', class: 'revoked' };
    if (key.expires_at && new Date(key.expires_at) < new Date()) {
      return { label: 'Expired', class: 'expired' };
    }
    return { label: 'Active', class: 'active' };
  };

  // Summary stats
  const activeKeys = keys.filter((k) => k.is_active && (!k.expires_at || new Date(k.expires_at) > new Date()));
  const totalRequests = keys.reduce((sum, k) => sum + k.usage_count, 0);

  if (isLoading && keys.length === 0) {
    return (
      <div className="api-key-management">
        <div className="loading-container">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="api-key-management">
      <div className="api-key-header">
        <div>
          <h1>API Key Management</h1>
          <p>{`Manage programmatic access to the ${BRANDING.productName} API`}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          + Create API Key
        </button>
      </div>

      {error && (
        <div className="error-container">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Summary Cards */}
      <div className="api-key-summary">
        <div className="summary-card">
          <div className="summary-icon">🔑</div>
          <div className="summary-content">
            <h3>Total Keys</h3>
            <div className="summary-value">{pagination.total}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">✅</div>
          <div className="summary-content">
            <h3>Active Keys</h3>
            <div className="summary-value">{activeKeys.length}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">📊</div>
          <div className="summary-content">
            <h3>Total Requests</h3>
            <div className="summary-value">{totalRequests.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="api-key-table-container">
        {keys.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔑</div>
            <h3>No API Keys</h3>
            <p>Create your first API key to enable programmatic access.</p>
          </div>
        ) : (
          <>
            <table className="api-key-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Scopes</th>
                  <th>Status</th>
                  <th>Last Used</th>
                  <th>Requests</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => {
                  const status = getStatus(key);
                  return (
                    <tr key={key.id}>
                      <td>
                        <strong>{key.name}</strong>
                        {key.description && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            {key.description}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="key-prefix">{key.key_prefix}...</span>
                      </td>
                      <td>
                        <div className="scope-badges">
                          {key.scopes.map((scope) => (
                            <span key={scope} className={`scope-badge ${scope}`}>
                              {scope}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${status.class}`}>{status.label}</span>
                      </td>
                      <td>
                        {key.last_used_at
                          ? new Date(key.last_used_at).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td>{key.usage_count.toLocaleString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn view"
                            onClick={() => handleViewDetails(key)}
                          >
                            View
                          </button>
                          {key.is_active && (
                            <>
                              <button
                                className="action-btn rotate"
                                onClick={() => setConfirmRotate(key)}
                              >
                                Rotate
                              </button>
                              <button
                                className="action-btn revoke"
                                onClick={() => setConfirmRevoke(key)}
                              >
                                Revoke
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="pagination">
              <div className="pagination-info">
                Showing {pagination.offset + 1}-{Math.min(pagination.offset + keys.length, pagination.total)} of{' '}
                {pagination.total}
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  disabled={pagination.offset === 0}
                  onClick={() =>
                    setPagination((p) => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))
                  }
                >
                  Previous
                </button>
                <button
                  className="pagination-btn"
                  disabled={!pagination.has_more}
                  onClick={() => setPagination((p) => ({ ...p, offset: p.offset + p.limit }))}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateKeyModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleKeyCreated}
          authenticatedFetch={authenticatedFetch}
        />
      )}

      {revealedKey && (
        <KeyRevealModal
          apiKey={revealedKey.key}
          record={revealedKey.record}
          onClose={() => setRevealedKey(null)}
        />
      )}

      {detailKey && (
        <DetailModal
          apiKey={detailKey.key}
          stats={detailKey.stats}
          onClose={() => setDetailKey(null)}
        />
      )}

      {confirmRevoke && (
        <ConfirmModal
          title="Revoke API Key"
          message={`Are you sure you want to revoke "${confirmRevoke.name}"? This action cannot be undone and any applications using this key will immediately lose access.`}
          confirmLabel="Revoke Key"
          onConfirm={handleRevoke}
          onCancel={() => setConfirmRevoke(null)}
          isLoading={isProcessing}
        />
      )}

      {confirmRotate && (
        <ConfirmModal
          title="Rotate API Key"
          message={`This will revoke "${confirmRotate.name}" and create a new key with the same settings. Update your applications with the new key immediately.`}
          confirmLabel="Rotate Key"
          confirmClass="btn-primary"
          onConfirm={handleRotate}
          onCancel={() => setConfirmRotate(null)}
          isLoading={isProcessing}
        />
      )}
    </div>
  );
};

export default ApiKeyManagement;
